import * as React from 'react';
import {
  ArrowUp,
  BookOpen,
  ChevronDown,
  FileText,
  Image as ImageIcon,
  ListChecks,
  Paperclip,
  PenLine,
  RefreshCw,
} from 'lucide-react';
import { detectTextDirection } from '../../utils/textUtils';
import { filterDocumentAttachments } from '../../utils/fileAttachments';
import './ChatComposer.css';

const TEXTAREA_MIN_PX = 40;
const TEXTAREA_MAX_PX = 160;

export type AgentComposerMode = 'suggestions' | 'draft';

export type ChatComposerSubmitPayload = {
  text: string;
  attachments: File[];
};

export interface ChatComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (payload: ChatComposerSubmitPayload) => void | Promise<void>;
  disabled?: boolean;
  submitting?: boolean;
  placeholder?: string;
  layout: 'initial' | 'sticky';
  label?: React.ReactNode;
  submitTitle?: string;
  /** Slightly gray card surface (e.g. after first message in thread) */
  followUp?: boolean;
  /** Agent behavior: review issues vs. draft into the document */
  agentMode?: AgentComposerMode;
  onAgentModeChange?: (mode: AgentComposerMode) => void;
  /** When set, show a book icon that calls this callback when clicked. */
  onPlaybookIconClick?: () => void;
  /** Selected playbook chip label — when set a chip is shown with an X to clear. */
  selectedPlaybookLabel?: string;
  onClearPlaybook?: () => void;
  /** Disable only the playbook icon (e.g. no project linked). */
  playbookDisabled?: boolean;
  /** Show Suggestions / Draft mode picker (Agent & Editor). Hide for clause Ask Agent. */
  showAgentModeSelector?: boolean;
}

export const ChatComposer: React.FC<ChatComposerProps> = ({
  value,
  onChange,
  onSubmit,
  disabled = false,
  submitting = false,
  placeholder = '',
  layout,
  label,
  submitTitle = 'Send',
  followUp = false,
  agentMode = 'suggestions',
  onAgentModeChange,
  onPlaybookIconClick,
  selectedPlaybookLabel,
  onClearPlaybook,
  playbookDisabled = false,
  showAgentModeSelector = true,
}) => {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const imageInputRef = React.useRef<HTMLInputElement>(null);
  const attachmentInputRef = React.useRef<HTMLInputElement>(null);
  const modeDropdownRef = React.useRef<HTMLDivElement>(null);
  const [imageFiles, setImageFiles] = React.useState<File[]>([]);
  const [attachmentFiles, setAttachmentFiles] = React.useState<File[]>([]);
  const [dragActive, setDragActive] = React.useState(false);
  const [agentModeMenuOpen, setAgentModeMenuOpen] = React.useState(false);

  const adjustTextareaHeight = React.useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const full = el.scrollHeight;
    const next = Math.min(Math.max(full, TEXTAREA_MIN_PX), TEXTAREA_MAX_PX);
    el.style.height = `${next}px`;
    el.style.overflowY = full > TEXTAREA_MAX_PX ? 'auto' : 'hidden';
  }, []);

  React.useLayoutEffect(() => {
    adjustTextareaHeight();
  }, [value, adjustTextareaHeight]);

  React.useEffect(() => {
    if (!agentModeMenuOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (modeDropdownRef.current?.contains(e.target as Node)) return;
      setAgentModeMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAgentModeMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [agentModeMenuOpen]);

  function trySubmit() {
    if (disabled || submitting) return;
    const t = value.trim();
    if (!t && attachmentFiles.length === 0 && !selectedPlaybookLabel) return;
    const attachments = [...attachmentFiles];
    setImageFiles([]);
    setAttachmentFiles([]);
    void onSubmit({ text: t, attachments });
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      trySubmit();
    }
  }

  function onPickImages(e: React.ChangeEvent<HTMLInputElement>) {
    const list = e.target.files;
    if (!list?.length) return;
    setImageFiles((prev) => [...prev, ...Array.from(list)]);
    e.target.value = '';
  }

  function removeImageAt(index: number) {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
  }

  function onPickAttachments(e: React.ChangeEvent<HTMLInputElement>) {
    const list = e.target.files;
    if (!list?.length) return;
    const next = filterDocumentAttachments(list);
    if (next.length) setAttachmentFiles((prev) => [...prev, ...next]);
    e.target.value = '';
  }

  function removeAttachmentAt(index: number) {
    setAttachmentFiles((prev) => prev.filter((_, i) => i !== index));
  }

  function onDragOver(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    e.dataTransfer.dropEffect = 'copy';
  }

  function onDragEnter(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    setDragActive(true);
  }

  function onDragLeave(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragActive(false);
    }
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (busy) return;
    const list = e.dataTransfer.files;
    if (!list?.length) return;
    const next = filterDocumentAttachments(list);
    if (next.length) setAttachmentFiles((prev) => [...prev, ...next]);
  }

  const busy = disabled || submitting;

  /** Match EditorChatInput: auto until user types, then Hebrew/Arabic → rtl */
  const inputDir: 'rtl' | 'ltr' | 'auto' = !value.trim()
    ? 'auto'
    : detectTextDirection(value);

  return (
    <div className={`ag-composer ${layout === 'sticky' ? 'ag-composer--sticky' : 'ag-composer--initial'}`}>
      {label}
      <div
        className={`ag-composer-card${followUp ? ' ag-composer-card--followup' : ''}${dragActive ? ' ag-composer-card--drag' : ''}`}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={onDrop}
      >
        {(imageFiles.length > 0 || attachmentFiles.length > 0 || selectedPlaybookLabel) && (
          <div className="ag-image-chips">
            {selectedPlaybookLabel && (
              <button
                type="button"
                className="ag-image-chip ag-playbook-chip"
                onClick={onClearPlaybook}
                title="Remove playbook context"
                aria-label="Remove playbook context"
              >
                <BookOpen size={12} aria-hidden />
                <span className="ag-image-chip-name">{selectedPlaybookLabel}</span>
              </button>
            )}
            {imageFiles.map((f, i) => (
              <button
                key={`img-${f.name}-${i}`}
                type="button"
                className="ag-image-chip"
                onClick={() => removeImageAt(i)}
                title="Remove image"
              >
                <ImageIcon size={12} aria-hidden />
                <span className="ag-image-chip-name">{f.name}</span>
              </button>
            ))}
            {attachmentFiles.map((f, i) => (
              <button
                key={`att-${f.name}-${i}`}
                type="button"
                className="ag-image-chip ag-attachment-chip"
                onClick={() => removeAttachmentAt(i)}
                title="Remove attachment"
              >
                <FileText size={12} aria-hidden />
                <span className="ag-image-chip-name">{f.name}</span>
              </button>
            ))}
          </div>
        )}
        <textarea
          ref={textareaRef}
          className="ag-composer-textarea"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={1}
          disabled={busy}
          dir={inputDir}
          title={
            followUp
              ? submitTitle
              : 'Enter to send · Shift+Enter for new line'
          }
        />
        <div className="ag-composer-toolbar">
          {!followUp && (
            <div className="ag-composer-toolbar-hint-row">
              <span className="ag-composer-toolbar-hint">
                <kbd className="ag-kbd">Enter</kbd> to send · <kbd className="ag-kbd">Shift</kbd>
                {' + '}
                <kbd className="ag-kbd">Enter</kbd> for new line
              </span>
            </div>
          )}
          <div className="ag-composer-toolbar-actions">
            {showAgentModeSelector && (
            <div className="ag-composer-toolbar-mode">
              <div
                ref={modeDropdownRef}
                className={`ag-mode-dropdown${followUp ? ' ag-mode-dropdown--placement-up' : ' ag-mode-dropdown--placement-down'}`}
                data-open={agentModeMenuOpen ? 'true' : 'false'}
                data-placement={followUp ? 'up' : 'down'}
              >
                <button
                  type="button"
                  className="ag-mode-dropdown-trigger"
                  disabled={busy}
                  aria-haspopup="listbox"
                  aria-expanded={agentModeMenuOpen}
                  aria-label="Agent mode"
                  title="Suggestions: review and issue list. Draft: generate or insert text into Word."
                  onClick={() => !busy && setAgentModeMenuOpen((o) => !o)}
                >
                  {agentMode === 'suggestions' ? (
                    <ListChecks size={14} strokeWidth={2} className="ag-mode-dropdown-icon" aria-hidden />
                  ) : (
                    <PenLine size={14} strokeWidth={2} className="ag-mode-dropdown-icon" aria-hidden />
                  )}
                  <span className="ag-mode-dropdown-label">
                    {agentMode === 'suggestions' ? 'Suggestions' : 'Draft'}
                  </span>
                  <ChevronDown
                    size={14}
                    strokeWidth={2}
                    className={`ag-mode-dropdown-chevron${agentModeMenuOpen ? ' ag-mode-dropdown-chevron--open' : ''}`}
                    aria-hidden
                  />
                </button>
                {agentModeMenuOpen && (
                  <ul className="ag-mode-dropdown-menu" role="listbox" aria-label="Choose agent mode">
                    <li role="option" aria-selected={agentMode === 'suggestions'}>
                      <button
                        type="button"
                        className={`ag-mode-dropdown-item${agentMode === 'suggestions' ? ' ag-mode-dropdown-item--active' : ''}`}
                        onClick={() => {
                          onAgentModeChange?.('suggestions');
                          setAgentModeMenuOpen(false);
                        }}
                      >
                        <ListChecks size={14} strokeWidth={2} aria-hidden />
                        <span>
                          <span className="ag-mode-dropdown-item-title">Suggestions</span>
                          <span className="ag-mode-dropdown-item-desc">Review document and list issues</span>
                        </span>
                      </button>
                    </li>
                    <li role="option" aria-selected={agentMode === 'draft'}>
                      <button
                        type="button"
                        className={`ag-mode-dropdown-item${agentMode === 'draft' ? ' ag-mode-dropdown-item--active' : ''}`}
                        onClick={() => {
                          onAgentModeChange?.('draft');
                          setAgentModeMenuOpen(false);
                        }}
                      >
                        <PenLine size={14} strokeWidth={2} aria-hidden />
                        <span>
                          <span className="ag-mode-dropdown-item-title">Draft</span>
                          <span className="ag-mode-dropdown-item-desc">Generate or insert text in Word</span>
                        </span>
                      </button>
                    </li>
                  </ul>
                )}
              </div>
            </div>
            )}
            <div className="ag-composer-toolbar-right">
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              multiple
              className="ag-hidden-file"
              onChange={onPickImages}
              aria-hidden
              tabIndex={-1}
            />
            <input
              ref={attachmentInputRef}
              type="file"
              accept=".docx,.pdf,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
              multiple
              className="ag-hidden-file"
              onChange={onPickAttachments}
              aria-hidden
              tabIndex={-1}
            />
            <button
              type="button"
              className="ag-icon-ghost"
              title="Upload image"
              disabled={busy}
              onClick={() => imageInputRef.current?.click()}
            >
              <ImageIcon size={18} strokeWidth={1.75} />
            </button>
            <button
              type="button"
              className="ag-icon-ghost"
              title="Attach document (.docx, .pdf, .txt)"
              disabled={busy}
              onClick={() => attachmentInputRef.current?.click()}
            >
              <Paperclip size={18} strokeWidth={1.75} />
            </button>
            {onPlaybookIconClick && (
              <button
                type="button"
                className={`ag-icon-ghost${selectedPlaybookLabel ? ' ag-icon-ghost--active' : ''}`}
                title={
                  playbookDisabled
                    ? 'Link a project to use playbooks'
                    : selectedPlaybookLabel
                    ? 'Change playbook context'
                    : 'Use a past-contract playbook as context'
                }
                disabled={busy || playbookDisabled}
                aria-label="Select playbook"
                onClick={onPlaybookIconClick}
              >
                <BookOpen size={18} strokeWidth={1.75} />
              </button>
            )}
            <button
              type="button"
              className="ag-send-circle"
              onClick={trySubmit}
              disabled={busy || (!value.trim() && attachmentFiles.length === 0 && !selectedPlaybookLabel)}
              title={submitTitle}
            >
              {submitting ? (
                <RefreshCw size={14} className="spinning ag-send-icon" />
              ) : (
                <ArrowUp size={14} strokeWidth={2.5} className="ag-send-icon" />
              )}
            </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
